/**
 * Demo 3 – Erfassungs-Flow Builder (Baum-Konzept)
 *
 * Strecke = Wurzel des Baums (übergeordneter Kontext)
 * Wäscheklassen = Kinder der Strecke
 * Artikelgruppen = Kinder der Wäscheklasse (n:m Zuordnung)
 * Substrecken = optionale Unterteilung der Strecke
 *
 * READ-ONLY: Keine Backend-Writes, nur lokaler Browser-State
 */

import React, { useState, useMemo, useEffect, useCallback, DragEvent } from 'react';
import {
  GitBranch, Route, Layers, Package, Users, Scale, X, Info, Search,
  Copy, RotateCcw, Loader2, GripVertical, Check, ChevronRight, ChevronDown,
  Play, AlertTriangle, TreePine, Minus, ArrowRight,
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

// =============================================================================
// TYPES
// =============================================================================

interface SubLine {
  id: string;
  name: string;
  sortOrder?: number;
}

interface ApiLine {
  id: string;
  name: string;
  isActive?: boolean;
  subLines?: SubLine[];
}

interface LaundryClassEntity {
  id: string;
  code: string;
  name: string;
  isActive?: boolean;
}

interface ArticleGroupEntity {
  id: string;
  code: string;
  name: string;
  isActive?: boolean;
}

/** Wäscheklasse im Baum mit zugeordneten Artikelgruppen */
interface TreeLaundryClass {
  id: string;
  articleGroupIds: string[];
}

/** Gesamte Flow-Konfiguration für eine Strecke */
interface FlowConfig {
  lineId: string | null;
  subLineIds: string[];
  laundryClasses: TreeLaundryClass[];
}

type DragKind = 'SUB_LINE' | 'LAUNDRY_CLASS' | 'ARTICLE_GROUP';

interface DragPayload {
  id: string;
  name: string;
  code?: string;
  kind: DragKind;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const emptyFlow = (): FlowConfig => ({
  lineId: null,
  subLineIds: [],
  laundryClasses: [],
});

// =============================================================================
// DRAGGABLE ITEM (left panel)
// =============================================================================

function DraggableItem({ payload, icon: Icon }: {
  payload: DragPayload;
  icon: React.ElementType;
}) {
  const handleDragStart = (e: DragEvent) => {
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="flex items-center gap-2 p-2.5 bg-white border rounded-lg cursor-grab hover:border-blue-300 hover:shadow-sm transition-all group"
    >
      <GripVertical className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
      <Icon className="w-4 h-4 text-gray-500" />
      <span className="flex-1 text-sm font-medium truncate">{payload.name}</span>
    </div>
  );
}

// =============================================================================
// DROP ZONE (generic)
// =============================================================================

function DropZone({ acceptKind, onDrop, children, emptyText, className, onClick }: {
  acceptKind: DragKind;
  onDrop: (p: DragPayload) => void;
  children?: React.ReactNode;
  emptyText: string;
  className?: string;
  onClick?: () => void;
}) {
  const [over, setOver] = useState(false);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(true);
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    try {
      const p = JSON.parse(e.dataTransfer.getData('application/json')) as DragPayload;
      if (p.kind === acceptKind) onDrop(p);
    } catch { /* invalid */ }
  };

  const hasChildren = React.Children.count(children) > 0;

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      className={[
        'transition-all rounded-lg',
        over && 'ring-2 ring-blue-400 bg-blue-50',
        className,
      ].filter(Boolean).join(' ')}
    >
      {hasChildren ? children : (
        <div
          onClick={onClick}
          className={`p-3 border-2 border-dashed rounded-lg text-center text-sm ${onClick ? 'cursor-pointer hover:border-blue-400 hover:text-blue-500' : ''} ${
            over ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-400'
          }`}
        >
          {over ? 'Hier ablegen...' : emptyText}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CHIP
// =============================================================================

function Chip({ label, code, onRemove, color = 'blue' }: {
  label: string;
  code?: string;
  onRemove: () => void;
  color?: 'blue' | 'green' | 'purple';
}) {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-800',
    green:  'bg-green-50 border-green-200 text-green-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-sm font-medium ${colors[color]}`}>
      {label}
      <button onClick={onRemove} className="ml-0.5 p-0.5 hover:bg-black/10 rounded-full">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// =============================================================================
// TREE: ARTICLE GROUP NODE
// =============================================================================

function ArticleGroupNode({ ag, onRemove }: {
  ag: ArticleGroupEntity;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 py-1 pl-2">
      <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
      <Package className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
      <span className="text-sm text-gray-700">{ag.name}</span>
      <button onClick={onRemove} className="ml-auto p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500">
        <Minus className="w-3 h-3" />
      </button>
    </div>
  );
}

// =============================================================================
// TREE: LAUNDRY CLASS NODE
// =============================================================================

function LaundryClassNode({ tlc, lc, articleGroups, onRemove, onAddAG, onRemoveAG, onAddAllAG, onFocusTab }: {
  tlc: TreeLaundryClass;
  lc: LaundryClassEntity;
  articleGroups: ArticleGroupEntity[];
  onRemove: () => void;
  onAddAG: (p: DragPayload) => void;
  onRemoveAG: (agId: string) => void;
  onAddAllAG: () => void;
  onFocusTab?: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const assignedAGs = tlc.articleGroupIds
    .map((id) => articleGroups.find((ag) => ag.id === id))
    .filter((x): x is ArticleGroupEntity => !!x);

  return (
    <div className="border rounded-lg bg-white">
      {/* Header */}
      <div className="flex items-center gap-2 p-3">
        <button onClick={() => setExpanded(!expanded)} className="p-0.5 hover:bg-gray-100 rounded">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        </button>
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Layers className="w-4 h-4 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate">{lc.name}</span>
            {!expanded && assignedAGs.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                {assignedAGs.length} Artikelgr.
              </span>
            )}
            {!expanded && assignedAGs.length === 0 && (
              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-400 rounded-full">
                ohne Artikelgr.
              </span>
            )}
          </div>
        </div>
        <button onClick={onRemove} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 border-t">
          {/* Artikelgruppen */}
          <div className="mt-2 ml-2 pl-3 border-l-2 border-green-200">
            {assignedAGs.length > 0 && (
              <div className="space-y-0.5 mb-2">
                {assignedAGs.map((ag) => (
                  <ArticleGroupNode key={ag.id} ag={ag} onRemove={() => onRemoveAG(ag.id)} />
                ))}
              </div>
            )}
            <DropZone
              acceptKind="ARTICLE_GROUP"
              onDrop={onAddAG}
              emptyText={assignedAGs.length === 0 ? 'Artikelgruppen hierher ziehen' : 'Weitere Artikelgruppe hierher'}
              onClick={onFocusTab}
            />
            {assignedAGs.length < articleGroups.length && (
              <button
                onClick={onAddAllAG}
                className="mt-2 w-full text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg py-1.5 hover:bg-green-100 transition-colors"
              >
                Alle Artikelgruppen zuordnen ({articleGroups.length - assignedAGs.length})
              </button>
            )}
            {assignedAGs.length === 0 && (
              <p className="mt-1.5 text-xs text-gray-400">Ohne Artikelgruppen wird dieser Schritt übersprungen</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LEFT PANEL
// =============================================================================

function LeftPanel({ activeTab, setActiveTab, subLines, laundryClasses, articleGroups, loading,
  flowConfig }: {
  activeTab: DragKind;
  setActiveTab: (t: DragKind) => void;
  subLines: SubLine[];
  laundryClasses: LaundryClassEntity[];
  articleGroups: ArticleGroupEntity[];
  loading: boolean;
  flowConfig: FlowConfig;
}) {
  const [search, setSearch] = useState('');

  // Reset search on tab change
  useEffect(() => { setSearch(''); }, [activeTab]);

  const tabs: { kind: DragKind; label: string; icon: React.ElementType; count: number }[] = [
    { kind: 'SUB_LINE', label: 'Substrecken', icon: Route, count: subLines.length },
    { kind: 'LAUNDRY_CLASS', label: 'Wäscheklassen', icon: Layers, count: laundryClasses.length },
    { kind: 'ARTICLE_GROUP', label: 'Artikelgruppen', icon: Package, count: articleGroups.length },
  ];

  const renderItems = () => {
    if (loading) {
      return (
        <div className="p-6 flex flex-col items-center text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mb-2" />
          <span className="text-sm">Lade Stammdaten...</span>
        </div>
      );
    }

    const q = search.toLowerCase();

    if (activeTab === 'SUB_LINE') {
      if (!flowConfig.lineId) {
        return (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center mx-3">
            <Info className="w-6 h-6 mx-auto mb-2 text-amber-500" />
            <p className="text-sm text-amber-700 font-medium">Zuerst eine Strecke wählen</p>
          </div>
        );
      }
      if (subLines.length === 0) {
        return (
          <div className="p-4 text-center mx-3">
            <Route className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">Diese Strecke hat keine Substrecken</p>
          </div>
        );
      }
      const filtered = subLines.filter((s) => s.name.toLowerCase().includes(q));
      const alreadyAdded = new Set(flowConfig.subLineIds);
      return (
        <div className="space-y-2 p-3">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Keine Treffer</p>
          ) : (
            filtered.map((s) => (
              <div key={s.id} className={alreadyAdded.has(s.id) ? 'opacity-40 pointer-events-none' : ''}>
                <DraggableItem
                  payload={{ id: s.id, name: s.name, kind: 'SUB_LINE' }}
                  icon={Route}
                />
              </div>
            ))
          )}
        </div>
      );
    }

    if (activeTab === 'LAUNDRY_CLASS') {
      const filtered = laundryClasses.filter((lc) =>
        lc.name.toLowerCase().includes(q) || lc.code.toLowerCase().includes(q)
      );
      const alreadyAdded = new Set(flowConfig.laundryClasses.map((t) => t.id));
      return (
        <div className="space-y-2 p-3">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Keine Treffer</p>
          ) : (
            filtered.map((lc) => (
              <div key={lc.id} className={alreadyAdded.has(lc.id) ? 'opacity-40 pointer-events-none' : ''}>
                <DraggableItem
                  payload={{ id: lc.id, name: lc.name, code: lc.code, kind: 'LAUNDRY_CLASS' }}
                  icon={Layers}
                />
              </div>
            ))
          )}
        </div>
      );
    }

    if (activeTab === 'ARTICLE_GROUP') {
      const filtered = articleGroups.filter((ag) =>
        ag.name.toLowerCase().includes(q) || ag.code.toLowerCase().includes(q)
      );
      return (
        <div className="space-y-2 p-3">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Keine Treffer</p>
          ) : (
            filtered.map((ag) => (
              <DraggableItem
                key={ag.id}
                payload={{ id: ag.id, name: ag.name, code: ag.code, kind: 'ARTICLE_GROUP' }}
                icon={Package}
              />
            ))
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map((t) => {
          const TIcon = t.icon;
          const active = activeTab === t.kind;
          return (
            <button
              key={t.kind}
              onClick={() => setActiveTab(t.kind)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                active ? 'border-blue-500 text-blue-700 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TIcon className="w-4 h-4" />
              <span>{t.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100'}`}>
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen..."
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto">
        {renderItems()}
      </div>
    </div>
  );
}

// =============================================================================
// STEPPER PREVIEW (right panel)
// =============================================================================

function StepperPreview({ flowConfig, lines, subLines, laundryClasses, articleGroups }: {
  flowConfig: FlowConfig;
  lines: ApiLine[];
  subLines: SubLine[];
  laundryClasses: LaundryClassEntity[];
  articleGroups: ArticleGroupEntity[];
}) {
  const [step, setStep] = useState(0);
  const [selectedLC, setSelectedLC] = useState<string | null>(null);

  const line = lines.find((l) => l.id === flowConfig.lineId);
  const hasSubLines = flowConfig.subLineIds.length > 0;
  const activeLCs = flowConfig.laundryClasses;

  // Build steps
  const steps: { label: string; icon: React.ElementType; detail: string; options?: string[] }[] = [];

  // Step: Strecke
  steps.push({
    label: 'Strecke',
    icon: GitBranch,
    detail: line ? `Vorausgewählt: ${line.name}` : 'Keine Strecke gewählt',
  });

  // Step: Substrecke (nur wenn vorhanden)
  if (hasSubLines) {
    const subNames = flowConfig.subLineIds
      .map((id) => subLines.find((s) => s.id === id)?.name)
      .filter(Boolean);
    steps.push({
      label: 'Substrecke',
      icon: Route,
      detail: `${subNames.length} zur Auswahl`,
      options: subNames as string[],
    });
  }

  // Step: Wäscheklasse
  if (activeLCs.length > 0) {
    const lcNames = activeLCs
      .map((tlc) => laundryClasses.find((lc) => lc.id === tlc.id)?.name)
      .filter(Boolean);
    steps.push({
      label: 'Wäscheklasse',
      icon: Layers,
      detail: `${lcNames.length} zur Auswahl`,
      options: lcNames as string[],
    });
  }

  // Step: Artikelgruppe (dynamisch nach WK)
  const selTLC = selectedLC ? activeLCs.find((t) => t.id === selectedLC) : null;
  if (selTLC && selTLC.articleGroupIds.length > 0) {
    const agNames = selTLC.articleGroupIds
      .map((id) => articleGroups.find((ag) => ag.id === id)?.name)
      .filter(Boolean);
    steps.push({
      label: 'Artikelgruppe',
      icon: Package,
      detail: `${agNames.length} zur Auswahl`,
      options: agNames as string[],
    });
  } else if (!selTLC && activeLCs.some((t) => t.articleGroupIds.length > 0)) {
    steps.push({
      label: 'Artikelgruppe',
      icon: Package,
      detail: 'Abhängig von Wäscheklasse',
    });
  }

  // System steps
  steps.push({ label: 'Kunde', icon: Users, detail: 'Auswahl zur Laufzeit' });
  steps.push({ label: 'Gewicht (kg)', icon: Scale, detail: 'Manuelle Eingabe' });

  // Reset step if out of bounds
  const safeStep = Math.min(step, steps.length - 1);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
          Vorarbeiter-Vorschau
        </h3>
        <p className="text-xs text-gray-500">So erlebt der Vorarbeiter den Ablauf</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Stepper */}
        <div className="space-y-1">
          {steps.map((s, i) => {
            const SIcon = s.icon;
            const isCurrent = i === safeStep;
            const isDone = i < safeStep;
            return (
              <button
                key={i}
                onClick={() => {
                  setStep(i);
                  if (s.label === 'Wäscheklasse') setSelectedLC(null);
                }}
                className={`w-full text-left rounded-lg p-3 transition-all ${
                  isCurrent
                    ? 'bg-blue-50 border-2 border-blue-500 shadow-sm'
                    : isDone
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-white border border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    isCurrent ? 'bg-blue-500 text-white'
                      : isDone ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <SIcon className={`w-4 h-4 flex-shrink-0 ${
                    isCurrent ? 'text-blue-600' : isDone ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${
                      isCurrent ? 'text-blue-900' : isDone ? 'text-green-800' : 'text-gray-700'
                    }`}>{s.label}</span>
                    <p className={`text-xs mt-0.5 ${
                      isCurrent ? 'text-blue-600' : isDone ? 'text-green-600' : 'text-gray-400'
                    }`}>{s.detail}</p>
                  </div>
                </div>

                {/* Show options for current step */}
                {isCurrent && s.options && s.options.length > 0 && (
                  <div className="mt-2 ml-10 flex flex-wrap gap-1.5">
                    {s.options.map((opt, oi) => (
                      <span
                        key={oi}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (s.label === 'Wäscheklasse') {
                            const lc = laundryClasses.find((l) => l.name === opt);
                            setSelectedLC(lc?.id || null);
                            setStep(i + 1);
                          }
                        }}
                        className={`px-2 py-0.5 text-xs rounded-full border cursor-pointer ${
                          s.label === 'Wäscheklasse'
                            ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                            : 'bg-gray-50 border-gray-200 text-gray-600'
                        }`}
                      >
                        {opt}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Abschluss */}
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-center">
          <ArrowRight className="w-5 h-5 mx-auto mb-1 text-gray-400" />
          <p className="text-sm font-medium text-gray-600">Erfassung abschliessen</p>
        </div>
      </div>

      {/* Reset preview */}
      <div className="p-3 border-t">
        <button
          onClick={() => { setStep(0); setSelectedLC(null); }}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-600 bg-white border rounded-lg hover:bg-gray-50"
        >
          <RotateCcw className="w-4 h-4" />
          Vorschau zurücksetzen
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function CaptureFlowBuilderDemo3Page() {
  // API data (READ-ONLY)
  const [apiLines, setApiLines] = useState<ApiLine[]>([]);
  const [apiLC, setApiLC] = useState<LaundryClassEntity[]>([]);
  const [apiAG, setApiAG] = useState<ArticleGroupEntity[]>([]);
  const [loading, setLoading] = useState(true);

  // Flow config (LOCAL ONLY)
  const [flowConfig, setFlowConfig] = useState<FlowConfig>(emptyFlow());

  // UI
  const [activeTab, setActiveTab] = useState<DragKind>('LAUNDRY_CLASS');
  const [toast, setToast] = useState(false);

  // Load API data
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [lRes, lcRes, agRes] = await Promise.all([
          fetch('/api/data/lines', { credentials: 'include' }),
          fetch('/api/data/laundry-classes', { credentials: 'include' }),
          fetch('/api/data/article-groups', { credentials: 'include' }),
        ]);
        if (lRes.ok) setApiLines(await lRes.json());
        if (lcRes.ok) setApiLC(await lcRes.json());
        if (agRes.ok) setApiAG(await agRes.json());
      } catch (e) {
        console.error('Fehler beim Laden:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Derived
  const selectedLine = apiLines.find((l) => l.id === flowConfig.lineId);
  const subLines = useMemo(() => selectedLine?.subLines || [], [selectedLine]);

  // Actions
  const selectLine = useCallback((lineId: string) => {
    setFlowConfig({
      lineId,
      subLineIds: [],
      laundryClasses: [],
    });
    setActiveTab('LAUNDRY_CLASS');
  }, []);

  const addSubLine = useCallback((p: DragPayload) => {
    setFlowConfig((prev) => {
      if (prev.subLineIds.includes(p.id)) return prev;
      return { ...prev, subLineIds: [...prev.subLineIds, p.id] };
    });
  }, []);

  const removeSubLine = useCallback((id: string) => {
    setFlowConfig((prev) => ({
      ...prev,
      subLineIds: prev.subLineIds.filter((s) => s !== id),
    }));
  }, []);

  const addLaundryClass = useCallback((p: DragPayload) => {
    setFlowConfig((prev) => {
      if (prev.laundryClasses.some((t) => t.id === p.id)) return prev;
      return {
        ...prev,
        laundryClasses: [...prev.laundryClasses, { id: p.id, articleGroupIds: [] }],
      };
    });
  }, []);

  const removeLaundryClass = useCallback((id: string) => {
    setFlowConfig((prev) => ({
      ...prev,
      laundryClasses: prev.laundryClasses.filter((t) => t.id !== id),
    }));
  }, []);


  const addArticleGroup = useCallback((lcId: string, p: DragPayload) => {
    setFlowConfig((prev) => ({
      ...prev,
      laundryClasses: prev.laundryClasses.map((t) =>
        t.id === lcId && !t.articleGroupIds.includes(p.id)
          ? { ...t, articleGroupIds: [...t.articleGroupIds, p.id] }
          : t
      ),
    }));
  }, []);

  const removeArticleGroup = useCallback((lcId: string, agId: string) => {
    setFlowConfig((prev) => ({
      ...prev,
      laundryClasses: prev.laundryClasses.map((t) =>
        t.id === lcId
          ? { ...t, articleGroupIds: t.articleGroupIds.filter((id) => id !== agId) }
          : t
      ),
    }));
  }, []);

  const addAllArticleGroups = useCallback((lcId: string) => {
    const allIds = apiAG.map((ag) => ag.id);
    setFlowConfig((prev) => ({
      ...prev,
      laundryClasses: prev.laundryClasses.map((t) =>
        t.id === lcId ? { ...t, articleGroupIds: allIds } : t
      ),
    }));
  }, [apiAG]);

  const addAllLaundryClasses = useCallback(() => {
    setFlowConfig((prev) => {
      const existingIds = new Set(prev.laundryClasses.map((t) => t.id));
      const newLCs = apiLC
        .filter((lc) => !existingIds.has(lc.id))
        .map((lc) => ({ id: lc.id, articleGroupIds: [] as string[] }));
      return { ...prev, laundryClasses: [...prev.laundryClasses, ...newLCs] };
    });
  }, [apiLC]);

  const copyJson = () => {
    const data = {
      lineId: flowConfig.lineId,
      lineName: selectedLine?.name || null,
      subLineIds: flowConfig.subLineIds,
      laundryClasses: flowConfig.laundryClasses.map((tlc) => {
        const lc = apiLC.find((l) => l.id === tlc.id);
        return {
          id: tlc.id,
          name: lc?.name,
          code: lc?.code,
          hasArticleGroups: tlc.articleGroupIds.length > 0,
          articleGroups: tlc.articleGroupIds.map((agId) => {
            const ag = apiAG.find((a) => a.id === agId);
            return { id: agId, name: ag?.name, code: ag?.code };
          }),
        };
      }),
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setToast(true);
    setTimeout(() => setToast(false), 2000);
  };

  const reset = () => {
    setFlowConfig(emptyFlow());
    setActiveTab('LAUNDRY_CLASS');
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                Demo 3 – Baum-Flow Builder
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full border border-emerald-300">
                  READ-ONLY
                </span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Strecke als Wurzel · Wäscheklassen als Äste · Artikelgruppen als Blätter
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={copyJson}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border rounded-lg hover:bg-gray-50">
                <Copy className="w-4 h-4" />JSON
              </button>
              <button onClick={reset}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-emerald-700 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-50">
                <RotateCcw className="w-4 h-4" />Zurücksetzen
              </button>
              <div className="flex items-center gap-2 bg-white border border-emerald-300 rounded-lg px-4 py-2">
                <AlertTriangle className="w-5 h-5 text-emerald-600" />
                <span className="text-sm text-emerald-800 font-medium">Nur Vorschau</span>
              </div>
            </div>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg flex items-center gap-2">
            <Check className="w-4 h-4" />JSON kopiert!
          </div>
        )}

        {/* 3-Column Layout */}
        <div className="flex h-[calc(100vh-88px)]">
          {/* LEFT: Stammdaten */}
          <div className="w-72 bg-white border-r flex flex-col">
            <div className="p-3 border-b bg-gray-50 text-xs text-gray-600 flex items-center gap-2">
              <GripVertical className="w-3.5 h-3.5" />
              Stammdaten in den Baum ziehen
            </div>
            <div className="flex-1 overflow-hidden">
              <LeftPanel
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                subLines={subLines}
                laundryClasses={apiLC}
                articleGroups={apiAG}
                loading={loading}
                flowConfig={flowConfig}
              />
            </div>
          </div>

          {/* MIDDLE: Tree */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-2xl mx-auto">
              {/* Strecken-Auswahl */}
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <TreePine className="w-4 h-4" />
                  Strecke (Wurzel)
                </h2>
                {loading ? (
                  <div className="p-6 bg-white rounded-xl border text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500">Lade Strecken...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {apiLines.map((line) => (
                      <button
                        key={line.id}
                        onClick={() => selectLine(line.id)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          flowConfig.lineId === line.id
                            ? 'border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-200'
                            : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            flowConfig.lineId === line.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <GitBranch className="w-4 h-4" />
                          </div>
                          <div>
                            <span className={`text-sm font-semibold ${
                              flowConfig.lineId === line.id ? 'text-emerald-900' : 'text-gray-700'
                            }`}>{line.name}</span>
                            {(line.subLines?.length || 0) > 0 && (
                              <p className="text-xs text-gray-400">{line.subLines?.length} Substrecken</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tree content (only when line selected) */}
              {flowConfig.lineId && (
                <div className="space-y-4">
                  {/* Connector from root */}
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-emerald-300" />
                  </div>

                  {/* Substrecken */}
                  {subLines.length > 0 && (
                    <div className="bg-white rounded-xl border p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Route className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Substrecken</h3>
                          <p className="text-xs text-gray-500">Optional: Unterteilung der Strecke</p>
                        </div>
                      </div>

                      {/* Assigned sublines */}
                      {flowConfig.subLineIds.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {flowConfig.subLineIds.map((slId) => {
                            const sl = subLines.find((s) => s.id === slId);
                            return sl ? (
                              <Chip key={sl.id} label={sl.name} onRemove={() => removeSubLine(sl.id)} color="blue" />
                            ) : null;
                          })}
                        </div>
                      )}

                      <DropZone
                        acceptKind="SUB_LINE"
                        onDrop={addSubLine}
                        emptyText={flowConfig.subLineIds.length === 0
                          ? 'Substrecken hierher ziehen'
                          : 'Weitere Substrecke hinzufügen'}
                        onClick={() => setActiveTab('SUB_LINE')}
                      />
                    </div>
                  )}

                  {/* Connector */}
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-purple-300" />
                  </div>

                  {/* Wäscheklassen + Artikelgruppen (Baumstruktur) */}
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Layers className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Wäscheklassen</h3>
                        <p className="text-xs text-gray-500">Jede Wäscheklasse kann eigene Artikelgruppen haben</p>
                      </div>
                      <span className="ml-auto px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                        {flowConfig.laundryClasses.length} zugeordnet
                      </span>
                    </div>

                    {/* Tree: Wäscheklassen */}
                    {flowConfig.laundryClasses.length > 0 && (
                      <div className="space-y-3 mb-3">
                        {flowConfig.laundryClasses.map((tlc) => {
                          const lc = apiLC.find((l) => l.id === tlc.id);
                          if (!lc) return null;
                          return (
                            <LaundryClassNode
                              key={tlc.id}
                              tlc={tlc}
                              lc={lc}
                              articleGroups={apiAG}
                              onRemove={() => removeLaundryClass(tlc.id)}
                              onAddAG={(p) => addArticleGroup(tlc.id, p)}
                              onRemoveAG={(agId) => removeArticleGroup(tlc.id, agId)}
                              onAddAllAG={() => addAllArticleGroups(tlc.id)}
                              onFocusTab={() => setActiveTab('ARTICLE_GROUP')}
                            />
                          );
                        })}
                      </div>
                    )}

                    <DropZone
                      acceptKind="LAUNDRY_CLASS"
                      onDrop={addLaundryClass}
                      emptyText={flowConfig.laundryClasses.length === 0
                        ? 'Wäscheklassen hierher ziehen'
                        : 'Weitere Wäscheklasse hinzufügen'}
                      onClick={() => setActiveTab('LAUNDRY_CLASS')}
                    />
                    {flowConfig.laundryClasses.length < apiLC.length && (
                      <button
                        onClick={addAllLaundryClasses}
                        className="mt-2 w-full text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg py-1.5 hover:bg-purple-100 transition-colors"
                      >
                        Alle Wäscheklassen zuordnen ({apiLC.length - flowConfig.laundryClasses.length})
                      </button>
                    )}
                  </div>

                  {/* Connector */}
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-gray-300" />
                  </div>

                  {/* Kunde */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                      <Users className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700">Kunde</h4>
                      <p className="text-xs text-gray-500">Auswahl zur Laufzeit</p>
                    </div>
                  </div>

                  {/* Connector */}
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-gray-300" />
                  </div>

                  {/* Gewicht */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                      <Scale className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700">Gewicht</h4>
                      <p className="text-xs text-gray-500">Manuelle Eingabe (kg)</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!flowConfig.lineId && !loading && (
                <div className="mt-8 p-8 bg-white rounded-xl border-2 border-dashed border-gray-300 text-center">
                  <TreePine className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-1">Strecke wählen</h3>
                  <p className="text-sm text-gray-400">Wähle oben eine Strecke als Wurzel des Baums</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Preview */}
          <div className="w-80 bg-gray-50 border-l">
            <StepperPreview
              flowConfig={flowConfig}
              lines={apiLines}
              subLines={subLines}
              laundryClasses={apiLC}
              articleGroups={apiAG}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
